/**
 * Applications Copilot — structured answer blocks (v2 "glass").
 *
 * One component per `block.kind` from COPILOT_API_CONTRACT.md:
 * candidate_list, comparison, metric_row, chart, timeline, evidence,
 * actions, empty, plus the G1 company-scope blocks `navigate` and
 * `jobs_list`, and the G2 blocks `report` (here) and `agenda`
 * (./AgendaBlock.jsx). Unknown kinds render nothing.
 *
 * Visual system: `cp-*` classes from ./copilot.css (see COPILOT_DESIGN_SPEC.md).
 * House style: compact enterprise rows, 12–13 px secondary text, thin bars.
 * Never a grid of large stat boxes. Colour is never the only signal — every
 * band colour is accompanied by a label or an icon.
 */
import React, { useId, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import {
  Eye,
  Crosshair,
  MessageSquare,
  Filter,
  Quote,
  Table2,
  BarChart3,
  Check,
  X,
  Inbox,
  ArrowUpRight,
  FileText,
  Mic,
  Briefcase,
  ExternalLink,
  MapPin,
  Users,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/Components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/Components/ui/table';
import { cn } from '@/lib/utils';
import { navigateTargetPath, useCopilot } from './CopilotContext';
import ScheduleTestBlock from './ScheduleTestBlock';
import PhoneScreenBlock from './PhoneScreenBlock';
import AgendaBlock from './AgendaBlock';

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

export function initialsOf(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Legacy Tailwind tone classes for a 0–100 score (kept for callers). */
export function scoreTone(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return 'bg-muted text-muted-foreground';
  if (n >= 80) return 'bg-success-foreground text-success-text';
  if (n >= 50) return 'bg-warning-foreground text-warning-text';
  return 'bg-error-foreground text-error-text';
}

/** Score band for `.cp-score-ring[data-band]`: ≥80 high, 60–79 mid, <60 low. */
export function scoreBand(score) {
  const n = Number(score);
  if (score === null || score === undefined || score === '' || !Number.isFinite(n)) return 'none';
  if (n >= 80) return 'high';
  if (n >= 60) return 'mid';
  return 'low';
}

// Company-side statuses exactly as the Applications page labels them
// (New / Viewed / Shortlisted / Interview Scheduled / Rejected / Selected / Hired),
// plus the snake_case keys; matched case-insensitively, anything else is neutral.
const STATUS_OK = new Set([
  'hired', 'selected', 'shortlisted', 'client_shortlisted', 'offered', 'offer_accepted', 'joined',
]);
const STATUS_BAD = new Set([
  'rejected', 'withdrawn', 'did_not_join', 'offer_declined', 'fail', 'client rejected', 'did not join',
]);

/** Status band: 'ok' | 'bad' | 'neutral'. */
export function statusBand(status) {
  const key = String(status || '').toLowerCase();
  if (STATUS_OK.has(key)) return 'ok';
  if (STATUS_BAD.has(key)) return 'bad';
  return 'neutral';
}

// Job statuses (jobs_list): Active/Open -> ok, Closed/Inactive -> bad, Hold/Paused/Draft -> neutral.
const JOB_STATUS_OK = new Set(['active', 'open', 'published', 'live']);
const JOB_STATUS_BAD = new Set(['inactive', 'closed', 'archived', 'expired', 'deleted']);

/** Job status band: 'ok' | 'bad' | 'neutral'. */
export function jobStatusBand(status) {
  const key = String(status || '').toLowerCase().trim();
  if (JOB_STATUS_OK.has(key)) return 'ok';
  if (JOB_STATUS_BAD.has(key)) return 'bad';
  return 'neutral';
}

const STATUS_DOT_COLOR = {
  ok: 'hsl(var(--cp-ok))',
  bad: 'hsl(var(--cp-bad))',
  neutral: 'hsl(var(--cp-teal))',
};

function humanStatus(status) {
  // Stages (snake_case) become words and are capitalised by CSS; company-set
  // statuses are shown verbatim, exactly as the recruiter typed them.
  return String(status || '').replace(/_/g, ' ').trim();
}

function formatNumber(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(1);
  return String(value);
}

function formatValue(value, unit) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') {
    const text = Number.isInteger(value) ? String(value) : value.toFixed(1);
    return unit ? `${text}${unit === '%' ? '%' : ` ${unit}`}` : text;
  }
  return unit ? `${value} ${unit}` : String(value);
}

function relativeTime(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return '';
  }
}

// ---------------------------------------------------------------------------
// small shared pieces
// ---------------------------------------------------------------------------

/** 32 px icon button with aria-label + tooltip (uses `.cp-icon-btn`). */
export function IconButton({ label, onClick, disabled, on, className, children, ...rest }) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label}
            data-on={on ? 'true' : undefined}
            onClick={onClick}
            disabled={disabled}
            className={cn('cp-icon-btn cp-focus disabled:opacity-40 disabled:pointer-events-none', className)}
            {...rest}
          >
            {children}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="px-2 py-1 text-[11px]">
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Initials avatar wrapped in a 2 px gradient ring. `size` is the outer size. */
export function InitialsAvatar({ name, size = 32, className }) {
  const inner = size - 4;
  return (
    <span
      className={cn('cp-gradient inline-flex shrink-0 items-center justify-center rounded-full p-[2px]', className)}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <span
        className="flex items-center justify-center rounded-full bg-background font-semibold text-foreground"
        style={{ width: inner, height: inner, fontSize: Math.max(9, Math.round(inner * 0.4)) }}
      >
        {initialsOf(name)}
      </span>
    </span>
  );
}

/** 28 px SVG score ring, colour by band via `data-band`, number centred. */
export function ScoreRing({ score, size = 28, stroke = 3, className }) {
  const band = scoreBand(score);
  const n = Number(score);
  const pct = band === 'none' ? 0 : Math.max(0, Math.min(100, n));
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct / 100);
  const label = band === 'none' ? 'No score' : `Score ${formatNumber(n)} out of 100`;
  return (
    <span
      className={cn('relative inline-flex shrink-0 items-center justify-center', className)}
      style={{ width: size, height: size }}
      role="img"
      aria-label={label}
      title={label}
    >
      <svg
        className="cp-score-ring"
        data-band={band}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
        focusable="false"
      >
        <circle className="track" cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} />
        <circle
          className="value"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span className="cp-num absolute inset-0 flex items-center justify-center text-[11px] font-semibold leading-none text-foreground">
        {band === 'none' ? '—' : Math.round(n)}
      </span>
    </span>
  );
}

/** Status pill: ok → mint + check, bad → rose + x, else neutral muted. `band` overrides the candidate mapping. */
export function StatusPill({ status, className, band: bandOverride }) {
  if (!status) return null;
  const band = bandOverride || statusBand(status);
  return (
    <span
      className={cn(
        'inline-flex h-[18px] shrink-0 items-center gap-1 rounded-full px-1.5 text-[11px] font-medium',
        /_/.test(String(status)) && 'capitalize',
        band === 'ok' && 'cp-pill-ok',
        band === 'bad' && 'cp-pill-bad',
        band === 'neutral' && 'bg-muted cp-muted',
        className
      )}
      data-band={band}
    >
      {band === 'ok' && <Check className="h-2.5 w-2.5" aria-hidden="true" />}
      {band === 'bad' && <X className="h-2.5 w-2.5" aria-hidden="true" />}
      {humanStatus(status)}
    </span>
  );
}

function BlockTitle({ title, className }) {
  if (!title) return null;
  return <div className={cn('text-xs font-semibold text-foreground/90', className)}>{title}</div>;
}

// ---------------------------------------------------------------------------
// candidate_list
// ---------------------------------------------------------------------------

export function CandidateListBlock({ block, onPrompt }) {
  const copilot = useCopilot();
  const items = Array.isArray(block.items) ? block.items : [];
  if (!items.length) return <EmptyBlock block={{ text: 'No candidates matched.' }} />;
  const hasFilter = block.filter && typeof block.filter === 'object' && Object.keys(block.filter).length > 0;
  return (
    <div className="space-y-1.5">
      {(block.title || hasFilter) && (
        <div className="flex items-center justify-between gap-2">
          <BlockTitle title={block.title} />
          {hasFilter && (
            <button
              type="button"
              className="cp-focus ml-auto inline-flex items-center gap-1 rounded text-[11px] font-medium text-primary hover:underline"
              onClick={() => copilot.applyFilter(block.filter)}
            >
              <Filter className="h-3 w-3" aria-hidden="true" /> Apply as filter
            </button>
          )}
        </div>
      )}
      <ul className="cp-stagger space-y-1.5">
        {items.map((item, idx) => {
          const appId = item.application_id;
          const noId = appId === undefined || appId === null;
          const facts = Array.isArray(item.facts) ? item.facts.slice(0, 2) : [];
          const factLine = [item.exp_years != null ? `${item.exp_years} yrs` : null, item.location || null, ...facts]
            .filter(Boolean)
            .join(' · ');
          const name = item.name || 'Candidate';
          // Cross-job results carry job_id/job_title; actions navigate to that job first.
          const itemJobId = item.job_id ?? item.jobId ?? null;
          const otherJob = itemJobId !== null && String(itemJobId) !== String(copilot.jobId ?? '');
          return (
            <li
              key={`${appId ?? 'c'}-${idx}`}
              style={{ '--i': idx }}
              className="cp-card cp-card-hover group flex min-h-[56px] items-center gap-2.5 px-2.5 py-2"
              data-job-id={itemJobId ?? undefined}
            >
              <InitialsAvatar name={name} size={32} />
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-[13px] font-semibold text-foreground" title={name}>{name}</span>
                  {otherJob && item.job_title && (
                    <span className="cp-chip cp-chip-static cp-chip-sm max-w-[45%] shrink-0" title={item.job_title} data-testid="copilot-job-tag">
                      <Briefcase className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
                      <span className="truncate">{item.job_title}</span>
                    </span>
                  )}
                </div>
                {(item.status || factLine) && (
                  <div className="flex min-w-0 items-center gap-1.5">
                    <StatusPill status={item.status} className="max-w-[60%] truncate" />
                    {factLine && <span className="truncate text-xs cp-muted" title={factLine}>{factLine}</span>}
                  </div>
                )}
                {item.reason && <div className="mt-0.5 text-[11px] leading-snug cp-muted">{item.reason}</div>}
              </div>
              {item.score !== undefined && item.score !== null && <ScoreRing score={item.score} />}
              <div className="flex shrink-0 items-center gap-0.5 transition-opacity duration-150 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100">
                <IconButton label="View profile" onClick={() => copilot.viewProfile(appId, itemJobId)} disabled={noId}>
                  <Eye className="h-4 w-4" aria-hidden="true" />
                </IconButton>
                <IconButton label="Locate in list" onClick={() => copilot.locateCandidate(appId, itemJobId)} disabled={noId}>
                  <Crosshair className="h-4 w-4" aria-hidden="true" />
                </IconButton>
                <IconButton
                  label={`Ask about ${name}`}
                  onClick={() => onPrompt && onPrompt(`About ${name}: `, { prefill: true, applicationId: appId, name: item.name })}
                >
                  <MessageSquare className="h-4 w-4" aria-hidden="true" />
                </IconButton>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// comparison
// ---------------------------------------------------------------------------

const STICKY_BG = { backgroundColor: 'var(--cp-glass-strong)' };
const BEST_BG = { backgroundColor: 'hsl(var(--cp-mint) / 0.14)' };

export function ComparisonBlock({ block }) {
  const copilot = useCopilot();
  const columns = Array.isArray(block.columns) ? block.columns : [];
  const rows = Array.isArray(block.rows) ? block.rows : [];
  const [metricIdx, setMetricIdx] = useState('0');
  if (!columns.length || !rows.length) return <EmptyBlock block={{ text: 'Nothing to compare.' }} />;
  const activeRow = rows[Number(metricIdx)] || rows[0];

  return (
    <div className="space-y-1.5">
      <BlockTitle title={block.title} />

      {/* Desktop / tablet: glass table */}
      <div className="cp-card hidden overflow-hidden sm:block">
        <div className="cp-scroll cp-fade-x overflow-x-auto">
          <Table className="text-xs">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="cp-label sticky left-0 z-10 h-9 px-2.5" style={STICKY_BG}>
                  Metric
                </TableHead>
                {columns.map((col, i) => (
                  <TableHead key={`${col.application_id ?? i}`} className="h-9 whitespace-nowrap px-2.5">
                    <button
                      type="button"
                      className="cp-focus inline-flex items-center gap-1.5 rounded text-left text-xs font-semibold text-foreground hover:underline"
                      onClick={() => copilot.viewProfile(col.application_id)}
                      aria-label={`Open profile of ${col.name || `candidate ${i + 1}`}`}
                    >
                      <InitialsAvatar name={col.name} size={20} />
                      <span className="truncate">{col.name || `#${i + 1}`}</span>
                    </button>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, ri) => (
                <TableRow key={`${row.label}-${ri}`}>
                  <TableCell
                    className="sticky left-0 z-10 whitespace-nowrap px-2.5 py-1.5 font-medium text-foreground/90"
                    style={STICKY_BG}
                  >
                    {row.label}
                  </TableCell>
                  {columns.map((_, ci) => {
                    const value = Array.isArray(row.values) ? row.values[ci] : undefined;
                    const best = Number.isInteger(row.best_index) && row.best_index === ci;
                    return (
                      <TableCell
                        key={ci}
                        title={row.source ? `Source: ${row.source}` : undefined}
                        data-best={best ? 'true' : undefined}
                        style={best ? BEST_BG : undefined}
                        className={cn('cp-num whitespace-nowrap px-2.5 py-1.5', best && 'font-semibold text-foreground')}
                      >
                        <span className="inline-flex items-center gap-1">
                          {formatValue(value)}
                          {best && <Check className="h-3 w-3 shrink-0" style={{ color: 'hsl(var(--cp-ok))' }} aria-label="Best" />}
                        </span>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Mobile: metric picker + stacked per-candidate cards */}
      <div className="space-y-1.5 sm:hidden">
        <Select value={metricIdx} onValueChange={setMetricIdx}>
          <SelectTrigger className="h-8 w-full text-xs" aria-label="Metric">
            <SelectValue placeholder="Metric" />
          </SelectTrigger>
          <SelectContent>
            {rows.map((row, ri) => (
              <SelectItem key={`${row.label}-${ri}`} value={String(ri)} className="text-xs">
                {row.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ul className="cp-stagger space-y-1.5">
          {columns.map((col, ci) => {
            const value = Array.isArray(activeRow.values) ? activeRow.values[ci] : undefined;
            const best = Number.isInteger(activeRow.best_index) && activeRow.best_index === ci;
            return (
              <li
                key={`${col.application_id ?? ci}`}
                style={{ '--i': ci, ...(best ? BEST_BG : null) }}
                data-best={best ? 'true' : undefined}
                className="cp-card flex items-center gap-2 px-2.5 py-2"
              >
                <InitialsAvatar name={col.name} size={24} />
                <button
                  type="button"
                  className="cp-focus min-w-0 flex-1 truncate rounded text-left text-[13px] font-semibold hover:underline"
                  onClick={() => copilot.viewProfile(col.application_id)}
                >
                  {col.name || `#${ci + 1}`}
                </button>
                <span className="cp-num inline-flex items-center gap-1 text-[13px] font-semibold" title={activeRow.source ? `Source: ${activeRow.source}` : undefined}>
                  {formatValue(value)}
                  {best && <Check className="h-3 w-3" style={{ color: 'hsl(var(--cp-ok))' }} aria-label="Best" />}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// metric_row — ONE slim card with thin gradient bars
// ---------------------------------------------------------------------------

export function MetricRowBlock({ block }) {
  const items = Array.isArray(block.items) ? block.items : [];
  if (!items.length) return null;
  return (
    <div className="cp-card px-3 py-2">
      <BlockTitle title={block.title} className="mb-1" />
      <ul className="divide-y divide-border/50">
        {items.map((item, idx) => {
          const hasMax = Number.isFinite(Number(item.max)) && Number(item.max) > 0;
          const pct = hasMax ? Math.max(0, Math.min(100, (Number(item.value) / Number(item.max)) * 100)) : null;
          return (
            <li key={`${item.label}-${idx}`} className="py-1.5 first:pt-0.5 last:pb-0.5">
              <div className="flex items-center justify-between gap-3">
                <span className="truncate text-xs cp-muted">{item.label}</span>
                <span className="cp-num shrink-0 text-[13px] font-semibold text-foreground">
                  {formatNumber(item.value)}
                  {item.unit && <span className="ml-0.5 font-normal cp-faint">{item.unit}</span>}
                  {hasMax && (
                    <span className="font-normal cp-faint">
                      {' '}/ {formatNumber(Number(item.max))}
                    </span>
                  )}
                </span>
              </div>
              {pct !== null && (
                <div
                  className="mt-1 h-1 w-full overflow-hidden rounded-full"
                  style={{ backgroundColor: 'hsl(var(--cp-green) / 0.10)' }}
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={Number(item.max)}
                  aria-valuenow={Number(item.value)}
                  aria-label={item.label}
                >
                  <div className="cp-gradient cp-bar-grow h-full rounded-full" style={{ width: `${pct}%` }} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// chart — recharts bar/line, 180 px high, Chart | Table segmented toggle
// ---------------------------------------------------------------------------

function GlassChartTooltip({ active, payload, label }) {
  if (!active || !Array.isArray(payload) || !payload.length) return null;
  return (
    <div className="cp-glass-strong rounded-lg px-2.5 py-1.5 text-[12px]">
      <div className="mb-0.5 font-semibold">{String(label ?? '')}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center justify-between gap-3">
          <span className="cp-muted">{p.name}</span>
          <span className="cp-num font-semibold">{formatValue(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

const MINT = 'hsl(165 60% 45%)';

export function ChartBlock({ block }) {
  const [showTable, setShowTable] = useState(false);
  const gradientId = `cp-chart-grad-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const data = Array.isArray(block.data) ? block.data : [];
  const series = Array.isArray(block.series) && block.series.length ? block.series : [];
  const xKey = block.x_key || 'x';
  const lineColors = useMemo(() => [MINT, 'hsl(178 48% 36%)', 'hsl(32 90% 44%)', 'hsl(164 10% 50%)'], []);
  if (!data.length || !series.length) return <EmptyBlock block={{ text: 'No data to chart.' }} />;
  const isLine = String(block.chart_type || 'bar').toLowerCase() === 'line';
  const Chart = isLine ? LineChart : BarChart;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <BlockTitle title={block.title} />
        <div className="ml-auto inline-flex items-center gap-1" role="group" aria-label="Chart view">
          <button
            type="button"
            className={cn('cp-chip !h-6 !px-2 text-[11px]', !showTable && 'cp-chip-mint')}
            aria-pressed={!showTable}
            onClick={() => setShowTable(false)}
          >
            <BarChart3 className="h-3 w-3" aria-hidden="true" /> Chart
          </button>
          <button
            type="button"
            className={cn('cp-chip !h-6 !px-2 text-[11px]', showTable && 'cp-chip-mint')}
            aria-pressed={showTable}
            onClick={() => setShowTable(true)}
          >
            <Table2 className="h-3 w-3" aria-hidden="true" /> Table
          </button>
        </div>
      </div>
      {showTable ? (
        <div className="cp-card overflow-hidden">
          <Table className="text-xs">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="cp-label h-8 px-2.5">{xKey}</TableHead>
                {series.map((s) => (
                  <TableHead key={s.key} className="cp-label h-8 px-2.5 text-right">
                    {s.label || s.key}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row, i) => (
                <TableRow key={i}>
                  <TableCell className="px-2.5 py-1.5">{String(row[xKey] ?? '')}</TableCell>
                  {series.map((s) => (
                    <TableCell key={s.key} className="cp-num px-2.5 py-1.5 text-right">
                      {formatValue(row[s.key])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="cp-card h-[180px] w-full px-1 pt-2" role="img" aria-label={block.title || 'Chart'}>
          <ResponsiveContainer width="100%" height={170}>
            <Chart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" className="cp-chart-stop-a" />
                  <stop offset="100%" className="cp-chart-stop-b" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(165 37% 22% / 0.14)" />
              <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: 'var(--cp-text-muted)' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--cp-text-muted)' }} tickLine={false} axisLine={false} allowDecimals={false} />
              <ChartTooltip cursor={{ fill: 'hsl(165 60% 45% / 0.08)' }} content={<GlassChartTooltip />} />
              {series.map((s, i) =>
                isLine ? (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label || s.key}
                    stroke={lineColors[i % lineColors.length]}
                    strokeWidth={2}
                    dot={{ r: 2.5, strokeWidth: 0, fill: lineColors[i % lineColors.length] }}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                  />
                ) : (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    name={s.label || s.key}
                    fill={i === 0 ? `url(#${gradientId})` : lineColors[i % lineColors.length]}
                    radius={[6, 6, 0, 0]}
                    maxBarSize={28}
                    isAnimationActive={false}
                  />
                )
              )}
            </Chart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// timeline
// ---------------------------------------------------------------------------

export function TimelineBlock({ block }) {
  const items = Array.isArray(block.items) ? block.items : [];
  if (!items.length) return <EmptyBlock block={{ text: 'No pipeline history yet.' }} />;
  return (
    <div className="space-y-1.5">
      <BlockTitle title={block.title} />
      <ol className="relative space-y-2.5 pl-5">
        <span className="cp-gradient absolute bottom-1.5 left-[5px] top-1.5 w-[2px] rounded-full opacity-70" aria-hidden="true" />
        {items.map((item, idx) => {
          const band = statusBand(item.status);
          return (
            <li key={`${item.at}-${idx}`} className="relative">
              <span
                className="absolute -left-5 top-[5px] h-3 w-3 rounded-full ring-2 ring-background"
                style={{ backgroundColor: STATUS_DOT_COLOR[band] }}
                data-band={band}
                aria-hidden="true"
              />
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[13px] font-medium capitalize">{humanStatus(item.status) || 'Update'}</span>
                <span className="cp-num cp-faint whitespace-nowrap text-right text-[11px]" title={item.at || undefined}>
                  {relativeTime(item.at)}
                </span>
              </div>
              {(item.by || item.note) && (
                <div className="text-xs cp-muted">{[item.by ? `by ${item.by}` : null, item.note].filter(Boolean).join(' · ')}</div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ---------------------------------------------------------------------------
// evidence
// ---------------------------------------------------------------------------

const SOURCE_LABEL = {
  resume: 'Resume',
  transcript: 'Interview transcript',
  phone_screen: 'Phone screen',
  feedback: 'Interviewer feedback',
  screening: 'Screening',
  coding: 'Coding test',
  test: 'Test',
  jd: 'Job description',
};

const SOURCE_ICON = {
  resume: FileText,
  transcript: Mic,
  phone_screen: Mic,
  screening: Mic,
  feedback: MessageSquare,
  jd: Briefcase,
};

export function EvidenceBlock({ block }) {
  const copilot = useCopilot();
  const items = Array.isArray(block.items) ? block.items : [];
  if (!items.length) return <EmptyBlock block={{ text: 'No supporting quotes found.' }} />;
  return (
    <div className="space-y-1.5">
      <BlockTitle title={block.title} />
      <div className="cp-stagger space-y-1.5">
        {items.map((item, idx) => {
          const sourceKey = String(item.source || '').toLowerCase();
          const Icon = SOURCE_ICON[sourceKey] || Quote;
          const hasApp = item.application_id !== undefined && item.application_id !== null;
          return (
            <figure
              key={`${item.ref || item.source}-${idx}`}
              style={{ '--i': idx }}
              className="cp-card relative overflow-hidden py-2 pl-3.5 pr-2.5"
            >
              <span className="cp-gradient absolute bottom-0 left-0 top-0 w-[3px]" aria-hidden="true" />
              <figcaption className="mb-1 flex items-center justify-between gap-2 text-[11px] cp-muted">
                <span className="inline-flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {SOURCE_LABEL[sourceKey] || item.source || 'Source'}
                </span>
                {hasApp && (
                  <button
                    type="button"
                    className="cp-focus inline-flex items-center gap-1 rounded hover:underline"
                    onClick={() => copilot.viewProfile(item.application_id)}
                  >
                    Open {sourceKey === 'resume' ? 'resume' : 'profile'}
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                  </button>
                )}
              </figcaption>
              <blockquote className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-foreground/90">
                {item.snippet}
              </blockquote>
            </figure>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// actions — chips; click → send prompt
// ---------------------------------------------------------------------------

export function ActionsBlock({ block, onPrompt }) {
  const items = Array.isArray(block.items) ? block.items : [];
  if (!items.length) return null;
  return (
    <div className="space-y-1.5">
      <BlockTitle title={block.title} />
      <div className="cp-stagger flex flex-wrap gap-2">
        {items.map((item, idx) => (
          <button
            key={`${item.label}-${idx}`}
            type="button"
            style={{ '--i': idx }}
            onClick={() => onPrompt && onPrompt(item.prompt || item.label)}
            className="cp-chip"
          >
            {item.label || item.prompt}
            <ArrowUpRight className="h-3 w-3 shrink-0 opacity-70" aria-hidden="true" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// empty
// ---------------------------------------------------------------------------

export function EmptyBlock({ block }) {
  return (
    <div className="cp-card flex items-center gap-2 border-dashed px-3 py-2.5 text-xs cp-muted" style={{ borderStyle: 'dashed' }}>
      <Inbox className="h-4 w-4 shrink-0 cp-faint" aria-hidden="true" />
      <span>{block?.text || 'No data for this yet.'}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// navigate — chip; click → the dock routes (nothing changes server-side)
// ---------------------------------------------------------------------------

export function NavigateBlock({ block }) {
  const copilot = useCopilot();
  const path = navigateTargetPath(block);
  if (!path) return null; // unknown target or a path outside /hirewithus/
  const label = block.label || (block.target === 'page' ? `Open ${path.replace(/^\/hirewithus\//, '')}` : 'Open');
  return (
    <button
      type="button"
      className="cp-chip cp-chip-mint cp-fade-up max-w-full"
      onClick={() => copilot.navigateTo(block)}
      data-testid="copilot-navigate"
      data-target={block.target}
      title={path}
    >
      <span className="truncate">{label}</span>
      <ArrowUpRight className="h-3 w-3 shrink-0 opacity-80" aria-hidden="true" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// jobs_list — compact job rows with an "Open" action
// ---------------------------------------------------------------------------

function jobFactValue(value) {
  const n = Number(value);
  return value !== null && value !== undefined && value !== '' && Number.isFinite(n) ? n : null;
}

export function JobsListBlock({ block }) {
  const copilot = useCopilot();
  const items = Array.isArray(block.items) ? block.items : [];
  if (!items.length) return <EmptyBlock block={{ text: 'No jobs matched.' }} />;
  return (
    <div className="space-y-1.5">
      <BlockTitle title={block.title} />
      <ul className="cp-stagger space-y-1.5" data-testid="copilot-jobs-list">
        {items.map((item, idx) => {
          const jobId = item.job_id ?? item.id ?? null;
          const title = item.title || item.job_title || (jobId !== null ? `Job ${jobId}` : 'Job');
          const where = [item.department || null, item.location || null].filter(Boolean).join(' · ');
          const extra = Array.isArray(item.facts) ? item.facts.filter(Boolean).slice(0, 2) : [];
          const detail = [where, ...extra].filter(Boolean).join(' · ');
          const counts = [
            ['applicants', jobFactValue(item.applicants)],
            ['unopened', jobFactValue(item.unopened)],
            ['shortlisted', jobFactValue(item.shortlisted)],
          ].filter(([, v]) => v !== null);
          const activity = relativeTime(item.last_activity || item.updated_at || item.created_at);
          return (
            <li
              key={`${jobId ?? 'j'}-${idx}`}
              style={{ '--i': idx }}
              className="cp-card cp-card-hover group flex min-h-[52px] items-center gap-2.5 px-2.5 py-2"
              data-job-id={jobId ?? undefined}
            >
              <span className="cp-gradient-soft cp-text-brand inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" aria-hidden="true">
                <Briefcase className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-[13px] font-semibold text-foreground" title={title}>{title}</span>
                  <StatusPill status={item.status} band={jobStatusBand(item.status)} className="max-w-[40%] truncate" />
                </div>
                {detail && (
                  <div className="flex min-w-0 items-center gap-1 text-xs cp-muted" title={detail}>
                    {item.location && <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />}
                    <span className="truncate">{detail}</span>
                  </div>
                )}
                {(counts.length > 0 || activity) && (
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] cp-muted">
                    {counts.map(([label, value]) => (
                      <span key={label} className="inline-flex items-center gap-1 whitespace-nowrap" data-testid={`copilot-job-${label}`}>
                        {label === 'applicants' && <Users className="h-3 w-3 shrink-0" aria-hidden="true" />}
                        <span className="cp-num font-semibold text-foreground">{value}</span> {label}
                      </span>
                    ))}
                    {activity && (
                      <span className="cp-num cp-faint ml-auto whitespace-nowrap" title={item.last_activity || undefined}>
                        {activity}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 items-center transition-opacity duration-150 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100">
                <IconButton
                  label={`Open ${title}`}
                  onClick={() => copilot.navigateTo({ target: 'job_applications', job_id: jobId, label: title })}
                  disabled={jobId === null}
                  data-testid="copilot-job-open"
                >
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </IconButton>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// report — ONE slim card of label / value lines (credits, team, offers summary)
// ---------------------------------------------------------------------------

/** 'negative' for "−52.30 cr" / "-3" / -3, 'positive' otherwise (null for blanks). */
export function valueSign(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value < 0 ? 'negative' : 'positive';
  return /^\s*[−-]\s*\d/.test(String(value)) ? 'negative' : 'positive';
}

/** Rows may arrive as `rows`, `data` or `items`, and a value under `value` or the first numeric field. */
export function reportRows(block) {
  const src = [block.rows, block.data, block.items].find((x) => Array.isArray(x) && x.length) || [];
  return src
    .filter((r) => r && typeof r === 'object')
    .map((r) => {
      let { value } = r;
      if (value === undefined || value === null) {
        const k = Object.keys(r).find((key) => !['label', 'name', 'hint', 'title'].includes(key) && (typeof r[key] === 'number' || typeof r[key] === 'string'));
        value = k ? r[k] : '';
      }
      return { label: r.label ?? r.name ?? r.title ?? '', value, hint: r.hint };
    })
    .filter((r) => r.label !== '' || r.value !== '');
}

export function ReportBlock({ block }) {
  const rows = reportRows(block);
  if (!rows.length && !block.footnote) return <EmptyBlock block={{ text: 'Nothing to report.' }} />;
  return (
    <div className="cp-card px-3 py-2" data-testid="copilot-report">
      <BlockTitle title={block.title} className="mb-1" />
      {rows.length > 0 && (
        <ul>
          {rows.map((row, idx) => {
            const sign = valueSign(row.value);
            return (
              <li key={`${row.label}-${idx}`} className="cp-report-row flex items-start justify-between gap-3 py-1.5 first:pt-0.5 last:pb-0.5">
                <span className="min-w-0">
                  <span className="block truncate text-xs cp-muted" title={row.label}>
                    {row.label}
                  </span>
                  {row.hint && (
                    <span className="block truncate text-[11px] leading-4 cp-faint" title={row.hint} data-testid="copilot-report-hint">
                      {row.hint}
                    </span>
                  )}
                </span>
                <span
                  className={cn('cp-num cp-report-value shrink-0 text-[13px] font-semibold', sign === 'negative' ? 'cp-text-warn' : 'text-foreground')}
                  data-sign={sign || undefined}
                >
                  {formatValue(row.value)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {block.footnote && (
        <div className={cn('text-[11px] leading-4 cp-muted', rows.length > 0 && 'mt-1 pt-1')} style={rows.length > 0 ? { borderTop: '1px solid var(--cp-glass-border)' } : undefined} data-testid="copilot-report-footnote">
          {block.footnote}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// dispatcher
// ---------------------------------------------------------------------------

// Rendered through `CopilotBlock` at render time (the module imports helpers
// from this one, so it must not be referenced while this module evaluates).
const LATE_BOUND_KINDS = new Set(['schedule_test', 'phone_screen', 'agenda']);

const BLOCK_COMPONENTS = {
  report: ReportBlock,
  candidate_list: CandidateListBlock,
  comparison: ComparisonBlock,
  metric_row: MetricRowBlock,
  chart: ChartBlock,
  timeline: TimelineBlock,
  evidence: EvidenceBlock,
  actions: ActionsBlock,
  empty: EmptyBlock,
  navigate: NavigateBlock,
  jobs_list: JobsListBlock,
};

export function CopilotBlock({ block, onPrompt, readOnly = false }) {
  if (!block || typeof block !== 'object') return null;
  // Resolved at render time (these modules import helpers from this module).
  if (block.kind === 'schedule_test') return <ScheduleTestBlock block={block} onPrompt={onPrompt} readOnly={readOnly} />;
  if (block.kind === 'phone_screen') return <PhoneScreenBlock block={block} onPrompt={onPrompt} readOnly={readOnly} />;
  if (block.kind === 'agenda') return <AgendaBlock block={block} />;
  const Component = BLOCK_COMPONENTS[block.kind];
  if (!Component) return null;
  return <Component block={block} onPrompt={onPrompt} />;
}

export function isKnownBlock(block) {
  return Boolean(block && (LATE_BOUND_KINDS.has(block.kind) || BLOCK_COMPONENTS[block.kind]));
}

export default function CopilotBlocks({ blocks, onPrompt, className, readOnly = false }) {
  const list = Array.isArray(blocks) ? blocks.filter(isKnownBlock) : [];
  if (!list.length) return null;
  return (
    <div className={cn('space-y-2.5', className)}>
      {list.map((block, idx) => (
        <div key={`${block.kind}-${idx}`} className="cp-fade-up">
          <CopilotBlock block={block} onPrompt={onPrompt} readOnly={readOnly} />
        </div>
      ))}
    </div>
  );
}
